import { PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'modules/queryBuilder/hooks/useTranslation';
import debounce from 'lodash/debounce';
import { InputText } from 'ui/inputs';
import { Search16 } from 'ui/icons/aurora/16px/Search16';
import { SelectValueType, BasicSelectWithNestedChildren } from 'ui/selects';
import noop from 'utils/noop';
import { isDefined } from 'utils/tsHelpers';
import {
  ExpandedSelectOptionsContainer,
  QueryBuilderSelectWrapper,
  StyledSelectOptionsContainer,
} from './QueryBuilderSelect.styles';
import { QueryBuilderSelectTrigger } from './QueryBuilderSelectTrigger';
import { ConditionSingleValueSelectProps } from '../types';
import { getSelectVariables } from '../utils';
import { TruncatedTooltipBasicOption } from '../TruncatedTooltipBasicOption';
import { useClickAway } from 'react-use';

export const QueryBuilderSelect = <TValue extends SelectValueType = string>({
  DefaultIcon,
  children = null,
  hasError = false,
  isLoading = false,
  onChange = noop,
  options = [],
  search,
  setSearch,
  value,
  variant,
  hasWarning,
  isVirtualized = false,
  isOpenByDefault,
  ...props
}: PropsWithChildren<ConditionSingleValueSelectProps<TValue>>) => {
  const { t } = useTranslation();
  const wrapperRef = useRef(null);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(isLoading);
  // if setSearch is defined opt for setSearch instead of internal filtering
  const disableInternalFiltering = isDefined(setSearch);

  const { baseOptions, selectedBaseOption } = useMemo(
    () => getSelectVariables(value, options, isVirtualized),
    [value, options, isVirtualized],
  );

  useEffect(() => {
    if (isInitialLoad && !isLoading) {
      setIsInitialLoad(false);
    }
  }, [isLoading, isInitialLoad]);

  useClickAway(wrapperRef, () => {
    if (setSearch) setSearch('');
  });

  return (
    <QueryBuilderSelectWrapper ref={wrapperRef} data-test="condition-dropdown-wrapper">
      <BasicSelectWithNestedChildren
        {...props}
        isVirtualized={isVirtualized}
        shouldOpenOnDefault={isOpenByDefault}
        Trigger={(triggerProps) => (
          <QueryBuilderSelectTrigger
            {...triggerProps}
            hasError={hasError ?? triggerProps.isError}
            hasWarning={hasWarning}
            isLoading={isInitialLoad && !selectedBaseOption.length}
            options={options}
            selectedOptions={selectedBaseOption}
            variant={variant}
            DefaultIcon={DefaultIcon}
          />
        )}
        options={baseOptions}
        Option={isVirtualized ? TruncatedTooltipBasicOption : undefined}
        onSearchChange={debounce((filterText) => {
          if (setSearch) setSearch(filterText);
        }, 500)}
        onSelect={([val]) => {
          onChange(val as TValue);
          if (setSearch) setSearch('');
        }}
        disableInternalFiltering={disableInternalFiltering}
        SelectOptionsContainer={
          isVirtualized ? ExpandedSelectOptionsContainer : StyledSelectOptionsContainer
        }
        shouldContainPopover
      >
        <InputText
          Prefix={<Search16 />}
          autoComplete="off"
          autoFocus
          id="user-profile-search"
          placeholder={t('storybook.placeholders.search')}
          type={setSearch ? 'text' : 'search'}
          onChange={setSearch}
          value={search ?? ''}
        />
        <>{children}</>
      </BasicSelectWithNestedChildren>
    </QueryBuilderSelectWrapper>
  );
};
